import {
  Component,
  Input,
  forwardRef,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Output,
  EventEmitter
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

// Overlay + Virtual Scroll
import { OverlayModule, Overlay, ConnectedPosition, ScrollStrategy } from '@angular/cdk/overlay';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

type NormItem = {
  raw: any;
  label: string;
  norm: string;
  baseOrder: number;
  sortKey: string;
};

@Component({
  selector: 'rms-search-select',
  standalone: true,
  templateUrl: './rms-search-select.component.html',
  styleUrls: ['../rms-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, CommonModule, OverlayModule, ScrollingModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RmsSearchSelectComponent),
      multi: true
    }
  ]
})
export class RmsSearchSelectComponent implements ControlValueAccessor {
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  // Inputs
  @Input() valueKey: string = 'value';
  @Input() labelKey: string = 'label';
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() name: string = '';
  @Input() sortBy: string | null = null;

  // Virtualization tunables
  @Input() optionHeight = 36;     // px per row
  @Input() maxPanelHeight = 330;  // max dropdown height in px
  @Output() selectionChange = new EventEmitter<any>();
  // Source options
  private _optionsRaw: any[] = [];
  @Input() set options(val: any[]) {
    this._optionsRaw = val ?? [];
    this.reindexAndNormalize();
    this.recompute();
    this.updateSelectedLabelFromValue();
    this.cdr.markForCheck();
  }
  get options(): any[] { return this._optionsRaw; }

  // Derived / filtered
  private _norm: NormItem[] = [];
  filteredOptions: any[] = [];

  // Search
  private _searchTerm = '';
  get searchTerm(): string { return this._searchTerm; }
  set searchTerm(v: string) {
    const next = v ?? '';
    if (next === this._searchTerm) return;
    this._searchTerm = next;
    this.search$.next(next);
  }
  private search$ = new Subject<string>();
  private collator = new Intl.Collator(undefined, { sensitivity: 'base' });

  // Dropdown state
  isOpen = false;
  activeIndex: number | null = null;

  // CVA
  value: any;
  selectedOptionLabel: string | null = null;
  onChange = (_: any) => { };
  onTouched = () => { };

  // Overlay config
  overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];
  scrollStrategy: ScrollStrategy;
  triggerWidth = 0;

  // Panel size (computed each open / search)
  panelHeight = 0;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay
  ) {
    this.search$
      .pipe(debounceTime(60), distinctUntilChanged())
      .subscribe(() => {
        this.recompute();
        this.resizePanel();
        this.ensureActiveInView(0); // keep top visible after a search
        this.cdr.markForCheck();
      });

    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
  }

  // ---------- Open/close ----------
  handleSelectedClick(origin: CdkOverlayOrigin) {
    this.toggleDropdown(origin);
  }

  toggleDropdown(origin: CdkOverlayOrigin) {
    this.isOpen = !this.isOpen && !this.disabled;
    if (this.isOpen) {
      this.cdr.markForCheck();
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.activeIndex = null;
    this.cdr.markForCheck();
  }

  onOverlayAttach(origin: CdkOverlayOrigin) {
    // Match panel width to trigger
    const rect = origin.elementRef.nativeElement.getBoundingClientRect();
    this.triggerWidth = Math.max(rect.width, 180);

    // Size the viewport based on results
    this.resizePanel();

    // Focus search + scroll to top after paint
    setTimeout(() => {
      this.searchInputRef?.nativeElement?.focus();
      this.viewport?.scrollToIndex(0);
    });
  }

  onOverlayDetach() {
    this.activeIndex = null;
  }

  // ---------- Keyboard on search ----------
  onSearchKeydown(event: KeyboardEvent) {
    const len = this.filteredOptions.length;

    if (event.key === 'ArrowDown' || event.key === 'Down') {
      event.preventDefault();
      if (!len) return;
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.min(this.activeIndex + 1, len - 1);
      this.ensureActiveInView();
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'Up') {
      event.preventDefault();
      if (!len) return;
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.max(this.activeIndex - 1, 0);
      this.ensureActiveInView();
      return;
    }

    if (event.key === 'Enter') {
      if (this.activeIndex != null && this.filteredOptions[this.activeIndex]) {
        event.preventDefault();
        this.selectOption(this.filteredOptions[this.activeIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  // ---------- Filtering / Sorting ----------
  private reindexAndNormalize(): void {
    const useSortKey = this.sortBy ?? this.labelKey;
    this._norm = this._optionsRaw.map((raw, idx) => {
      const label = String(raw?.[this.labelKey] ?? '');
      const norm = this.stripDiacritics(label).toLowerCase();
      const sortKey = String(raw?.[useSortKey] ?? '');
      return { raw, label, norm, baseOrder: idx, sortKey };
    });

    if (this.sortBy) {
      this._norm.sort((a, b) => this.collator.compare(a.sortKey, b.sortKey));
      this._norm.forEach((o, i) => (o.baseOrder = i));
    }
  }

  private recompute(): void {
    const q = this.stripDiacritics(this._searchTerm).toLowerCase().trim();
    if (!q) {
      this.filteredOptions = this._norm.map(o => o.raw);
      return;
    }

    const starts: Array<{ item: NormItem; pos: number }> = [];
    const contains: Array<{ item: NormItem; pos: number }> = [];

    for (const o of this._norm) {
      const pos = o.norm.indexOf(q);
      if (pos === -1) continue;
      (pos === 0 ? starts : contains).push({ item: o, pos });
    }

    const cmp = (a: { item: NormItem; pos: number }, b: { item: NormItem; pos: number }) => {
      if (a.pos !== b.pos) return a.pos - b.pos;
      if (this.sortBy) {
        const s = this.collator.compare(a.item.sortKey, b.item.sortKey);
        if (s !== 0) return s;
      }
      return a.item.baseOrder - b.item.baseOrder;
    };

    starts.sort(cmp);
    contains.sort(cmp);
    this.filteredOptions = [...starts, ...contains].map(x => x.item.raw);
  }

  private stripDiacritics(s: string): string {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // ---------- Selection / CVA ----------
  selectOption(option: any) {
    this.searchTerm = '';
    this.isOpen = false;
    this.value = option?.[this.valueKey];
    this.selectedOptionLabel = option?.[this.labelKey] ?? null;
    this.onChange(this.value);
    this.onTouched();
    this.cdr.markForCheck();
    this.selectionChange.emit(this.value);
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.updateSelectedLabelFromValue();
  }

  private updateSelectedLabelFromValue(): void {
    if (this.value === null || this.value === undefined) {
      this.selectedOptionLabel = null;
      return;
    }
    const match = this._optionsRaw.find(opt => opt?.[this.valueKey] == this.value);
    this.selectedOptionLabel = match ? match[this.labelKey] : null;
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  getSelectedLabel(): string {
    const match = this._optionsRaw.find(opt => opt?.[this.valueKey] == this.value);
    return match ? String(match[this.labelKey]) : '';
  }

  // ---------- Virtual scroll helpers ----------
  trackByValueKey = (_: number, item: any) => item?.[this.valueKey];

  private resizePanel(): void {
    // Height = min(maxPanelHeight, rows * rowHeight). If you want at least N rows, clamp here.
    const rows = this.filteredOptions?.length ?? 0;
    this.panelHeight = Math.min(this.maxPanelHeight, rows * this.optionHeight || this.optionHeight * 6);
  }

  private ensureActiveInView(fallbackIndex: number | null = null): void {
    if (this.activeIndex == null && fallbackIndex != null) {
      this.activeIndex = fallbackIndex;
    }
    if (this.activeIndex != null) {
      this.viewport?.scrollToIndex(this.activeIndex, 'smooth');
    }
  }
}
